import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import { Send, Trash2, Terminal, PanelLeftClose, PanelLeftOpen, MessageSquare, Wand2, BookOpen, Copy, Check, Menu } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { API_BASE } from '../config.tsx';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const remarkPluginsList = [remarkGfm, remarkMath];
const rehypePluginsList = [[rehypeKatex, { strict: false }], [rehypeHighlight, { detect: false }]];

const MessageBubble = React.memo(({ msg, onHandoff }: { msg: Message, onHandoff?: (mode: Mode, text: string) => void }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={cn(
        "flex w-full group relative",
        msg.role === 'user' ? "justify-end" : "justify-start"
      )}
    >
      <div
        className={cn(
          "max-w-[95%] md:max-w-[75%] rounded-2xl px-4 py-2 shadow-sm border relative",
          msg.role === 'user'
            ? "bg-gray-800 text-gray-100 border-cyan-500/50 shadow-[0_0_10px_rgba(6,182,212,0.3)] rounded-br-none"
            : "bg-gray-800 text-gray-100 border-purple-500/50 shadow-[0_0_10px_rgba(168,85,247,0.3)] rounded-bl-none prose prose-invert max-w-none break-words [overflow-wrap:anywhere] prose-a:break-all pr-10"
        )}
      >
        {msg.role === 'assistant' && (
          <button
            onClick={handleCopy}
            className="absolute top-2 right-2 p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:text-purple-400 border border-transparent hover:border-purple-500/50 hover:shadow-[0_0_8px_rgba(168,85,247,0.6)]"
            title="Copy message"
          >
            {copied ? <Check size={16} className="drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]" /> : <Copy size={16} className="drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]" />}
          </button>
        )}
        {msg.role === 'assistant' ? (
          <ReactMarkdown
            remarkPlugins={remarkPluginsList}
            rehypePlugins={rehypePluginsList as any}
            components={{
              code: ({ node, inline, className, children, ...props }: any) => {
                const match = /language-(\w+)/.exec(className || '');
                if (!inline && match) {
                  return (
                    <CodeBlock className={className} onHandoff={onHandoff} {...props}>
                      {children}
                    </CodeBlock>
                  );
                }
                return (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              }
            }}
          >
            {msg.content}
          </ReactMarkdown>
        ) : (
          <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] prose-a:break-all max-w-full">{msg.content}</div>
        )}
      </div>
    </motion.div>
  );
});


type Mode = 'default' | 'optimizer' | 'research';
type ChatState = Record<Mode, Message[]>;
const defaultChatState: ChatState = { default: [], optimizer: [], research: [] };

interface CodeBlockProps extends React.HTMLAttributes<HTMLPreElement> {
  children?: React.ReactNode;
  className?: string;
  onHandoff?: (mode: Mode, text: string) => void;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ children, className, onHandoff, ...props }) => {
  const [copied, setCopied] = useState(false);

  const extractText = (node: any): string => {
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) return node.map(extractText).join('');
    if (node?.props?.children) return extractText(node.props.children);
    return '';
  };

  const handleCopy = () => {
    const textStr = extractText(children);
    navigator.clipboard.writeText(textStr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isOptimizerDefault = className?.includes('language-optimized-prompt-default');
  const isOptimizerResearch = className?.includes('language-optimized-prompt-research');

  if (isOptimizerDefault || isOptimizerResearch) {
    return (
      <div className="relative group my-4 rounded-xl border overflow-hidden bg-gray-900 border-pink-500/30 shadow-[0_0_15px_rgba(236,72,153,0.15)]">
        <div className="p-4 overflow-y-auto max-h-[400px]">
          <pre className={cn(className, "whitespace-pre-wrap break-words m-0 p-0 bg-transparent text-gray-200 font-mono text-sm leading-relaxed")} {...props}>
            {children}
          </pre>
        </div>
        <div className="bg-gray-800/80 border-t border-gray-700/50 p-3 flex flex-col sm:flex-row gap-2 justify-between items-center">
          <button
            onClick={handleCopy}
            className="flex items-center justify-center gap-2 px-3 py-1.5 min-h-[44px] min-w-[44px] w-full sm:w-auto text-sm font-medium rounded-lg transition-all duration-300 text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 border border-gray-600/50 shadow-sm hover:shadow-[0_0_8px_rgba(156,163,175,0.6)] group"
          >
            {copied ? <Check size={16} className="text-green-400 drop-shadow-[0_0_8px_rgba(74,222,128,0.8)]" /> : <Copy size={16} className="group-hover:drop-shadow-[0_0_8px_rgba(156,163,175,0.8)]" />}
            {copied ? 'Copied!' : 'Copy Prompt'}
          </button>
          {onHandoff && (
            <button
              onClick={() => {
                const textStr = extractText(children);
                onHandoff(isOptimizerDefault ? 'default' : 'research', textStr);
              }}
              className="flex items-center justify-center gap-2 px-3 py-1.5 min-h-[44px] min-w-[44px] w-full sm:w-auto text-sm font-medium rounded-lg transition-all duration-300 text-white bg-pink-600 hover:bg-pink-500 shadow-[0_0_10px_rgba(236,72,153,0.5)] hover:shadow-[0_0_15px_rgba(236,72,153,0.8)]"
            >
              <Send size={16} className="drop-shadow-[0_0_8px_rgba(236,72,153,0.8)]" />
              {isOptimizerDefault ? 'Send to Standard Chat' : 'Send to Cursory Research'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative group">
      <pre className={cn(className, "overflow-x-auto")} {...props}>
        {children}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md bg-gray-800/80 text-gray-400 hover:text-white hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-all border border-gray-600/50 hover:border-gray-500 shadow-sm hover:shadow-[0_0_8px_rgba(156,163,175,0.6)]"
        title="Copy code"
      >
        {copied ? <Check size={16} className="text-green-400 drop-shadow-[0_0_8px_rgba(74,222,128,0.8)]" /> : <Copy size={16} className="group-hover:drop-shadow-[0_0_8px_rgba(156,163,175,0.8)]" />}
      </button>
    </div>
  );
};


const ChatInterface: React.FC = () => {
  const [chatState, setChatState] = useState<ChatState>(() => {
    const saved = sessionStorage.getItem('chatMessages');
    if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
             return { ...defaultChatState, default: parsed };
        }
        return parsed;
    }
    return defaultChatState;
  });
  const [activeMode, setActiveMode] = useState<Mode>('default');
  const [optimizerTargetMode, setOptimizerTargetMode] = useState<'default' | 'research' | null>(null);
  const messages = chatState[activeMode] || [];

  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConfirmingReset, setIsConfirmingReset] = useState(false);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    let prevIsMobile = window.innerWidth < 768;
    const checkMobile = () => {
      const mobile = window.innerWidth < 768; // md breakpoint
      setIsMobile(mobile);
      if (mobile && !prevIsMobile) {
         setSidebarOpen(false); // Close when transitioning to mobile
      }
      if (!mobile && prevIsMobile) {
         setSidebarOpen(false); // Fix for resize state trap when crossing to md
      }
      prevIsMobile = mobile;
    };

    checkMobile();
    // We can also initialize sidebarOpen purely based on initial load
    setSidebarOpen(!prevIsMobile);

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = () => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 240)}px`;
    }
  };

  useLayoutEffect(() => {
    adjustTextareaHeight();
  }, [input]);
  // Virtual Keyboard auto-scroll logic
  useEffect(() => {
    const handleVisualViewportResize = () => {
      if (messagesEndRef.current && window.visualViewport) {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
      }
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      sessionStorage.setItem('chatMessages', JSON.stringify(chatState));
    }, 500);
    return () => clearTimeout(timer);
  }, [chatState]);

  useLayoutEffect(() => {
    // Smart auto-scroll using ResizeObserver to catch inner content changes
    // (e.g. streaming tokens, math rendering, images)
    const container = messagesContainerRef.current;
    if (!container) return;

    let rafId: number;

    const handleResize = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // If user is within 150px of the bottom, auto-scroll. Otherwise, let them read.
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
      if (isNearBottom) {
        // Prevent layout thrashing by using requestAnimationFrame
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          if (typeof container.scrollTo === 'function') {
            container.scrollTo({
              top: container.scrollHeight,
              behavior: 'auto'
            });
          } else {
            container.scrollTop = container.scrollHeight;
          }
        });
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    // Observe the first child (if it exists) which wraps all messages
    if (container.firstElementChild) {
        resizeObserver.observe(container.firstElementChild);
    } else {
        resizeObserver.observe(container);
    }

    // Trigger initial check
    handleResize();

    return () => {
      resizeObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [messages]);

  const resetChat = () => {
    setChatState(prev => {
      const newState = { ...prev, [activeMode]: [] };
      sessionStorage.setItem('chatMessages', JSON.stringify(newState));
      return newState;
    });
    if (activeMode === 'optimizer') {
      setOptimizerTargetMode(null);
    }
    setIsConfirmingReset(false);
  };

  const handleHandoff = (targetMode: Mode, text: string) => {
    setActiveMode(targetMode);
    setInput(prev => prev ? `${prev}\n\n${text}` : text);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const sendMessage = async () => {
    if (!input.trim() || isStreaming) return;

    const userMsg: Message = { role: 'user', content: input };
    const currentMessages = [...messages];
    setChatState(prev => ({ ...prev, [activeMode]: [...currentMessages, userMsg] }));
    setInput('');
    setIsStreaming(true);

    // Force scroll to bottom when user explicitly sends a message
    setTimeout(() => {
      if (messagesContainerRef.current) {
        if (typeof messagesContainerRef.current.scrollTo === 'function') {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: 'auto'
          });
        } else {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }
    }, 10);

    try {
      const payload: any = {
          message: input,
          history: currentMessages,
          mode: activeMode
      };
      if (activeMode === 'optimizer' && optimizerTargetMode) {
          payload.optimizer_target = optimizerTargetMode;
      }

      const response = await fetch(`${API_BASE}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to send message');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';

      setChatState(prev => ({ ...prev, [activeMode]: [...prev[activeMode], { role: 'assistant', content: '' }] }));

      if (reader) {
        let lastUpdateTime = Date.now();
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // Ensure final content is rendered when complete
            setChatState(prev => {
              const modeMessages = prev[activeMode];
              const last = modeMessages[modeMessages.length - 1];
              return { ...prev, [activeMode]: [...modeMessages.slice(0, -1), { ...last, content: assistantContent }] };
            });
            break;
          }
          const chunk = decoder.decode(value, { stream: true });
          assistantContent += chunk;

          const now = Date.now();
          if (now - lastUpdateTime > 50) {
            setChatState(prev => {
              const modeMessages = prev[activeMode];
              const last = modeMessages[modeMessages.length - 1];
              return { ...prev, [activeMode]: [...modeMessages.slice(0, -1), { ...last, content: assistantContent }] };
            });
            lastUpdateTime = now;
          }
        }
      }

    } catch (err) {
      console.error(err);
      alert("Failed to communicate with the server. Please try again.");
    } finally {
      setIsStreaming(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sidebarContent = (
    <>
      <div className="p-4 flex items-center h-[73px] border-b border-gray-800/50 shrink-0">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={cn("p-2 -ml-2 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-gray-900 transition-colors", isMobile && "hidden")}
        >
          {sidebarOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
        </button>
        {sidebarOpen && (
          <span className={cn("font-semibold text-sm tracking-wide text-gray-300", !isMobile && "ml-3")}>MODES</span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-4 px-2 space-y-2">
        <button
          onClick={() => { setActiveMode('default'); if (isMobile) setSidebarOpen(false); }}
          className={cn(
            "w-full flex items-center p-3 rounded-xl transition-all duration-300 group",
            activeMode === 'default' ? "bg-gray-800 shadow-[0_0_15px_rgba(6,182,212,0.15)] border border-cyan-500/30 text-white" : "text-gray-400 hover:bg-gray-900 hover:text-gray-200 border border-transparent"
          )}
          title="Standard Chat"
        >
          <MessageSquare size={20} className={cn("shrink-0", activeMode === 'default' ? "text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.8)]" : "")} />
          {sidebarOpen && <span className="ml-3 text-sm font-medium whitespace-normal break-words text-left">Standard Chat</span>}
        </button>

        <button
          onClick={() => { setActiveMode('optimizer'); if (isMobile) setSidebarOpen(false); }}
          className={cn(
            "w-full flex items-center p-3 rounded-xl transition-all duration-300 group",
            activeMode === 'optimizer' ? "bg-gray-800 shadow-[0_0_15px_rgba(236,72,153,0.15)] border border-pink-500/30 text-white" : "text-gray-400 hover:bg-gray-900 hover:text-gray-200 border border-transparent"
          )}
          title="Prompt Optimizer"
        >
          <Wand2 size={20} className={cn("shrink-0", activeMode === 'optimizer' ? "text-pink-400 drop-shadow-[0_0_8px_rgba(236,72,153,0.8)]" : "")} />
          {sidebarOpen && <span className="ml-3 text-sm font-medium whitespace-normal break-words text-left">Prompt Optimizer</span>}
        </button>

        <button
          onClick={() => { setActiveMode('research'); if (isMobile) setSidebarOpen(false); }}
          className={cn(
            "w-full flex items-center p-3 rounded-xl transition-all duration-300 group",
            activeMode === 'research' ? "bg-gray-800 shadow-[0_0_15px_rgba(168,85,247,0.15)] border border-purple-500/30 text-white" : "text-gray-400 hover:bg-gray-900 hover:text-gray-200 border border-transparent"
          )}
          title="Cursory Research"
        >
          <BookOpen size={20} className={cn("shrink-0", activeMode === 'research' ? "text-purple-400 drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]" : "")} />
          {sidebarOpen && <span className="ml-3 text-sm font-medium whitespace-normal break-words text-left">Cursory Research</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-[100dvh] bg-gray-900 overflow-hidden">
      {/* Mobile Backdrop Overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop (collapsible) & Mobile (off-canvas) */}
      <motion.div
        initial={false}
        animate={
          isMobile
            ? { x: sidebarOpen ? 0 : "-100%", width: 260 }
            : { width: sidebarOpen ? 260 : 64, x: 0 }
        }
        transition={{ duration: 0.3, ease: "easeOut" }}
        className={cn(
          "h-full bg-gray-950 border-r border-gray-800 flex flex-col shrink-0 overflow-hidden",
          isMobile ? "fixed left-0 top-0 bottom-0 z-50 w-[260px]" : "relative z-10"
        )}
      >
        {sidebarContent}
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 relative h-full">
        {/* Top Banner for Standard Chat */}
        {activeMode === 'default' && (
          <div
            onClick={() => setActiveMode('optimizer')}
            className="w-full bg-gradient-to-r from-pink-600/20 via-purple-600/20 to-cyan-600/20 border-b border-pink-500/30 p-2 cursor-pointer hover:from-pink-600/30 hover:via-purple-600/30 hover:to-cyan-600/30 transition-all shrink-0 group relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full duration-1000 transition-transform ease-in-out" />
            <div className="flex items-center justify-center gap-2">
              <Wand2 size={16} className="text-pink-400 animate-pulse" />
              <span className="text-xs md:text-sm font-medium text-gray-200 group-hover:text-white transition-colors truncate">
                <strong className="text-pink-400 drop-shadow-[0_0_8px_rgba(236,72,153,0.8)]">New</strong> - Try the Prompt Optimizer
              </span>
            </div>
          </div>
        )}

        <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-900 z-10 shrink-0 h-[73px]">
          <div className="flex items-center gap-3">
             {isMobile && (
               <button
                 onClick={() => setSidebarOpen(true)}
                 className="p-1 -ml-1 text-gray-400 hover:text-white transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
               >
                 <Menu size={24} />
               </button>
             )}
             <h1 className="font-semibold text-lg bg-gradient-to-r from-cyan-400 to-pink-500 bg-clip-text text-transparent drop-shadow-[0_0_8px_rgba(236,72,153,0.5)]">chat.devin</h1>
          </div>

          {isConfirmingReset ? (
            <div className="flex items-center gap-2 text-sm text-red-400 bg-gray-800 px-3 py-1.5 rounded-md border border-red-900/50">
              <span>Are you sure?</span>
              <button
                onClick={resetChat}
                className="min-h-[44px] min-w-[44px] font-bold hover:text-red-300 px-1 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(248,113,113,0.8)]"
              >
                [Yes]
              </button>
              <button
                onClick={() => setIsConfirmingReset(false)}
                className="min-h-[44px] min-w-[44px] font-bold hover:text-gray-300 px-1 text-gray-400 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(156,163,175,0.8)]"
              >
                [No]
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsConfirmingReset(true)}
              className="min-h-[44px] min-w-[44px] flex items-center gap-2 px-3 py-1.5 text-sm text-red-400 hover:bg-gray-800 rounded-md transition-all duration-300 border border-transparent hover:border-red-900 hover:shadow-[0_0_8px_rgba(248,113,113,0.6)]"
              disabled={!messages || messages.length === 0}
            >
              <Trash2 size={16} className="drop-shadow-[0_0_8px_rgba(248,113,113,0.8)]" />
              <span className="hidden sm:inline">Reset Chat</span>
            </button>
          )}
        </div>

        {/* Messages */}
        <div
          ref={messagesContainerRef}
          className={cn("flex-1 overflow-y-auto p-2 md:p-4 bg-gray-900 min-h-0", isMobile && sidebarOpen && "overflow-hidden")}
        >
          <div className="space-y-4 pb-4 h-max min-h-full flex flex-col justify-end">
            <div className="flex-1" />
          {!messages || messages.length === 0 ? (
            activeMode === 'optimizer' ? (
              optimizerTargetMode === null ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="flex flex-col items-center justify-center h-full text-gray-400 gap-6 w-full max-w-4xl mx-auto py-8"
                >
                  <p className="text-xl md:text-2xl font-bold bg-gradient-to-r from-pink-400 to-purple-500 bg-clip-text text-transparent drop-shadow-[0_0_12px_rgba(236,72,153,0.4)] text-center px-4">
                    What are we optimizing for?
                  </p>
                  <div className="flex flex-col md:flex-row gap-4 w-full px-4 justify-center">
                    <button
                      onClick={() => setOptimizerTargetMode('default')}
                      className="flex-1 max-w-full md:max-w-[300px] flex flex-col items-center p-6 md:p-8 rounded-2xl bg-gray-800/50 border border-cyan-500/30 hover:border-cyan-500/80 hover:bg-gray-800 transition-all duration-300 shadow-[0_0_20px_rgba(6,182,212,0.15)] hover:shadow-[0_0_30px_rgba(6,182,212,0.3)] group cursor-pointer"
                    >
                      <MessageSquare size={40} className="md:w-[48px] md:h-[48px] text-cyan-400 drop-shadow-[0_0_12px_rgba(6,182,212,0.6)] mb-4 group-hover:scale-110 transition-transform" />
                      <span className="text-lg font-semibold text-gray-200">Standard Chat</span>
                      <span className="text-sm text-gray-400 mt-2 text-center">Flesh out an idea for general discussion and code.</span>
                    </button>
                    <button
                      onClick={() => setOptimizerTargetMode('research')}
                      className="flex-1 max-w-full md:max-w-[300px] flex flex-col items-center p-6 md:p-8 rounded-2xl bg-gray-800/50 border border-purple-500/30 hover:border-purple-500/80 hover:bg-gray-800 transition-all duration-300 shadow-[0_0_20px_rgba(168,85,247,0.15)] hover:shadow-[0_0_30px_rgba(168,85,247,0.3)] group cursor-pointer"
                    >
                      <BookOpen size={40} className="md:w-[48px] md:h-[48px] text-purple-400 drop-shadow-[0_0_12px_rgba(168,85,247,0.6)] mb-4 group-hover:scale-110 transition-transform" />
                      <span className="text-lg font-semibold text-gray-200">Cursory Research</span>
                      <span className="text-sm text-gray-400 mt-2 text-center">Refine a topic for an interactive Wikipedia-style primer.</span>
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="flex flex-col items-center justify-center h-full text-gray-400 gap-4"
                >
                  <div className="p-5 rounded-2xl bg-gray-800/50 border border-pink-500/30 shadow-[0_0_30px_rgba(236,72,153,0.15)] flex items-center justify-center">
                    <Wand2 size={40} className="md:w-[48px] md:h-[48px] text-pink-400 drop-shadow-[0_0_12px_rgba(236,72,153,0.6)]" />
                  </div>
                  <p className="text-xl md:text-2xl font-bold bg-gradient-to-r from-pink-400 to-purple-500 bg-clip-text text-transparent drop-shadow-[0_0_12px_rgba(236,72,153,0.4)] text-center px-4">
                    Prompt Optimizer
                  </p>
                  <p className="text-sm mt-2 text-center max-w-md px-4 leading-relaxed text-gray-300">
                    Answer a few quick questions to generate the perfect prompt. Once generated, simply send it to your mode of choice.
                  </p>
                </motion.div>
              )
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="flex flex-col items-center justify-center h-full text-gray-400 gap-4"
              >
                <div className="p-4 rounded-2xl bg-gray-800/50 border border-cyan-500/30 shadow-[0_0_20px_rgba(6,182,212,0.15)] flex items-center justify-center">
                  {activeMode === 'research' ? <BookOpen size={40} className="md:w-[48px] md:h-[48px] text-purple-400 drop-shadow-[0_0_12px_rgba(168,85,247,0.6)]" /> : <Terminal size={40} className="md:w-[48px] md:h-[48px] text-cyan-400 drop-shadow-[0_0_12px_rgba(6,182,212,0.6)]" />}
                </div>
                <p className={cn("text-xl md:text-2xl font-bold bg-gradient-to-r bg-clip-text text-transparent text-center px-4", activeMode === 'research' ? "from-purple-400 to-pink-500 drop-shadow-[0_0_12px_rgba(168,85,247,0.4)]" : "from-cyan-400 to-pink-500 drop-shadow-[0_0_12px_rgba(236,72,153,0.4)]")}>
                  {activeMode === 'research' ? "Cursory Research" : "Welcome to chat.devin"}
                </p>
                <p className="text-sm mt-2 text-center max-w-md px-4 leading-relaxed text-gray-300">
                  {activeMode === 'research'
                    ? "Explore topics in a conversational, interactive way. Ask anything to begin your deep dive."
                    : "A free single-session streaming AI chat platform powered by the latest OpenAI ChatGPT API. Explore conversations and seek instant answers."}
                </p>
              </motion.div>
            )
          ) : (
            <AnimatePresence initial={false}>
              {messages.map((msg, i) => (
                <MessageBubble key={i} msg={msg} onHandoff={handleHandoff} />
              ))}
            </AnimatePresence>
          )}
          <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Segmented Control for Optimizer Mode */}
        {activeMode === 'optimizer' && optimizerTargetMode !== null && (
          <div className="w-full flex justify-center py-2 bg-gray-900 shrink-0 border-t border-gray-800/50 z-10 relative">
            <div className="flex bg-gray-800 p-1 rounded-xl shadow-inner border border-gray-700/50 max-w-full overflow-x-auto">
              <button
                onClick={() => setOptimizerTargetMode('default')}
                disabled={messages.length > 0}
                className={cn(
                  "min-h-[44px] min-w-[44px] px-3 md:px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap",
                  optimizerTargetMode === 'default'
                    ? "bg-cyan-600/20 text-cyan-400 border border-cyan-500/50 shadow-[0_0_10px_rgba(6,182,212,0.2)]"
                    : "text-gray-400 hover:text-gray-200 hover:bg-gray-700 border border-transparent",
                  messages.length > 0 && "opacity-60 cursor-not-allowed hover:bg-transparent hover:text-gray-400"
                )}
              >
                Standard Chat
              </button>
              <button
                onClick={() => setOptimizerTargetMode('research')}
                disabled={messages.length > 0}
                className={cn(
                  "min-h-[44px] min-w-[44px] px-3 md:px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap",
                  optimizerTargetMode === 'research'
                    ? "bg-purple-600/20 text-purple-400 border border-purple-500/50 shadow-[0_0_10px_rgba(168,85,247,0.2)]"
                    : "text-gray-400 hover:text-gray-200 hover:bg-gray-700 border border-transparent",
                  messages.length > 0 && "opacity-60 cursor-not-allowed hover:bg-transparent hover:text-gray-400"
                )}
              >
                Cursory Research
              </button>
            </div>
          </div>
        )}

        {/* Input */}
        <div
          className="p-2 md:p-4 w-full bg-gray-900 border-t border-gray-800 shrink-0"
          style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
        >
          <div className="max-w-4xl mx-auto w-full">
            <motion.div
              animate={isStreaming ? {
                boxShadow: ['0px 0px 10px rgba(6,182,212,0.4)', '0px 0px 20px rgba(236,72,153,0.6)', '0px 0px 10px rgba(6,182,212,0.4)'],
                borderColor: ['rgba(55,65,81,1)', 'rgba(236,72,153,0.5)', 'rgba(55,65,81,1)']
              } : {
                boxShadow: '0px 0px 0px rgba(0,0,0,0)',
                borderColor: 'rgba(55,65,81,1)'
              }}
              transition={isStreaming ? { duration: 2, repeat: Infinity, ease: "easeInOut" } : { duration: 0.3 }}
              className="relative flex items-end gap-2 bg-gray-800 rounded-2xl p-2 pl-4 border border-gray-700 focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500 focus-within:shadow-[0_0_15px_rgba(6,182,212,0.5)] transition-all"
            >
              <label htmlFor="chat-input" className="sr-only">Type a message</label>
              <textarea
                id="chat-input"
                ref={inputRef}
                rows={1}
                className={cn(
                  "flex-1 bg-transparent border-none focus:ring-0 resize-none py-2 max-h-[200px] md:max-h-[240px] overflow-y-auto outline-none text-gray-100 placeholder-gray-500 text-base",
                  activeMode === 'optimizer' && optimizerTargetMode === null && "cursor-not-allowed opacity-50"
                )}
                placeholder={activeMode === 'optimizer' && optimizerTargetMode === null ? "Select an optimization target to begin..." : "Message..."}
                value={input}
                onChange={e => {
                  setInput(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                disabled={activeMode === 'optimizer' && optimizerTargetMode === null}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isStreaming}
                className={cn(
                  "p-2 w-11 h-11 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full transition-all duration-300 shrink-0",
                  input.trim() && !isStreaming
                    ? "bg-cyan-600 text-white hover:bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.5)] hover:shadow-[0_0_15px_rgba(6,182,212,0.8)]"
                    : "text-gray-500 bg-gray-700"
                )}
              >
                <Send size={20} className={cn(input.trim() && !isStreaming && "drop-shadow-[0_0_8px_rgba(6,182,212,0.8)]")} />
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
